import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Table as MuiTable,
    TableCell as MuiTableCell,
    TableCell,
    TableFooter as MuiTableFooter,
    TableRow as MuiTableRow,
    TableRow,
} from '@material-ui/core';

import { formatDate, formatDateTime } from '@klp/helpers';
import { getFromStorage, saveToStorage } from '@klp/utils';

import type { ITableConsumerState } from './ITableConsumerState';
import { TableBody } from './TableBody';
import type { TableColumnDef } from './TableColumnDef';
import { TableContext } from './TableContext';
import { TableExport } from './TableExport';
import { TableFilter } from './TableFilter';
import type { ITableFilterDef, TableFilterDef } from './TableFilterDef';
import { TableHead } from './TableHead';
import { TablePagination } from './TablePagination';

interface IOwnProps<T, F> {
    id: string;
    data: Array<T>;
    columns: Array<TableColumnDef<T, F>>;
    showAllWhenNoFilter?: boolean;
    alwaysShowFooter?: boolean;
    highlightPhrase?: string;
    onRowSelect?: (row: T) => void;
    exportName?: string;
}

interface ITablePaginationInitialProps {
    initialPage?: number;
    initialRowsPerPage?: number;
    initialRowsPerPageOptions?: Array<number>;
}

export type TableSortDirection = 'asc' | 'desc';

interface ITableSortProps<T, F> {
    sortBy?: TableColumnDef<T, F>;
    sortByDirection?: TableSortDirection;
}

interface ITableFilterProps<F> {
    filter?: Array<ITableFilterDef<F>>;
    activeFilter?: Array<string>;
    emptyTableMessage?: string;
}

export type ITableProps<DataType, FilterType> = IOwnProps<
    DataType,
    FilterType
> &
    ITablePaginationInitialProps &
    ITableFilterProps<FilterType> &
    ITableSortProps<DataType, FilterType>;

export const Table = <DataType = any, FilterType = any>(
    props: ITableProps<DataType, FilterType>,
): React.ReactElement => {
    const {
        id,
        exportName,
        data,
        columns,
        filter,
        alwaysShowFooter,
        emptyTableMessage,
        highlightPhrase,
        onRowSelect,
        activeFilter: initialActiveFilter = [],
        initialPage = 0,
        initialRowsPerPage = 25,
        initialRowsPerPageOptions = [10, 25, 50],
        sortBy,
        sortByDirection = 'desc',
        ...restProps
    } = props;

    const { t } = useTranslation();
    const [page, setPage] = useState(initialPage);
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const [rowsPerPageOptions] = useState(initialRowsPerPageOptions);
    const [activeFilter, setActiveFilter] = useState(() => {
        const storedFilter = getFromStorage(id);
        if (Array.isArray(storedFilter)) {
            return storedFilter;
        }
        return initialActiveFilter;
    });
    const [currentSortBy, setSortBy] = useState(sortBy);
    const [currentSortByDirection, setSortByDirection] =
        useState(sortByDirection);

    const handleChangePage = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>, newPage: number) => {
            event.preventDefault();
            setPage(newPage);
        },
        [],
    );

    const handleChangeRowsPerPage = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        },
        [],
    );

    const handleChangeSort = useCallback(
        (column: TableColumnDef<DataType, FilterType>) => () => {
            setSortBy(column);
            setSortByDirection((prevDirection) => {
                if (currentSortBy && column.key === currentSortBy.key) {
                    return prevDirection === 'asc' ? 'desc' : 'asc';
                }
                return 'asc';
            });
            setPage(
                currentSortBy && column.key === currentSortBy.key ? page : 0,
            );
        },
        [currentSortBy, page],
    );

    const handleChangeFilter = useCallback(
        (_filterKey: string, filterGroupName: string) => () => {
            setActiveFilter((prevActiveFilter) => {
                let updatedFilter;
                if (prevActiveFilter.includes(filterGroupName)) {
                    updatedFilter = prevActiveFilter.filter(
                        (item) => item !== filterGroupName,
                    );
                } else {
                    updatedFilter = [...prevActiveFilter, filterGroupName];
                }
                saveToStorage(id, updatedFilter);
                return updatedFilter;
            });
        },
        [id],
    );

    const paginate = useCallback(
        <T,>(list: Array<T>): Array<T> => {
            return list.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage,
            );
        },
        [page, rowsPerPage],
    );

    const sortDataComparison = useCallback(
        (
            sortDirection: TableSortDirection,
            valueA: number,
            valueB: number,
        ): number => {
            return valueA === valueB
                ? 0
                : sortDirection === 'asc'
                  ? valueA - valueB
                  : valueB - valueA;
        },
        [],
    );

    const sortDataComparisonString = useCallback(
        (
            sortDirection: TableSortDirection,
            valueA: string,
            valueB: string,
        ): number => {
            return valueA === valueB
                ? 0
                : sortDirection === 'asc'
                  ? (valueA || '').localeCompare(valueB || '')
                  : (valueB || '').localeCompare(valueA || '');
        },
        [],
    );

    const sortData = useCallback(
        <T,>(list: Array<T>): Array<T> => {
            if (!currentSortByDirection || !currentSortBy) return list;
            return list.sort((a: T, b: T) => {
                switch (currentSortBy.type) {
                    case 'date':
                        return sortDataComparisonString(
                            currentSortByDirection,
                            formatDate(new Date(a[currentSortBy.key])),
                            formatDate(new Date(b[currentSortBy.key])),
                        );
                    case 'dateTime':
                        return sortDataComparisonString(
                            currentSortByDirection,
                            formatDateTime(new Date(a[currentSortBy.key])),
                            formatDateTime(new Date(b[currentSortBy.key])),
                        );
                    case 'number':
                    case 'amount':
                        return sortDataComparison(
                            currentSortByDirection,
                            Number(a[currentSortBy.key]),
                            Number(b[currentSortBy.key]),
                        );
                    case 'boolean':
                        return sortDataComparison(
                            currentSortByDirection,
                            a[currentSortBy.key] ? 1 : 0,
                            b[currentSortBy.key] ? 1 : 0,
                        );
                    default:
                        return sortDataComparisonString(
                            currentSortByDirection,
                            a[currentSortBy.key],
                            b[currentSortBy.key],
                        );
                }
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentSortBy, currentSortByDirection],
    );

    const getFilterSettings = useCallback(
        (filterKey: string): Array<TableFilterDef<FilterType>> | [] => {
            return (filter ?? []).filter(
                (filterDef: TableFilterDef<FilterType>) =>
                    Object.keys(filterDef.filterGroup).includes(filterKey),
            );
        },
        [filter],
    );

    const filterData = useCallback(
        <T,>(list: Array<T>): Array<T> => {
            let filteredList: Array<T> =
                filter?.length && activeFilter.length > 0
                    ? []
                    : // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      props.showAllWhenNoFilter || !filter
                      ? list
                      : [];

            activeFilter?.forEach((filterKey) => {
                const filterSettings = getFilterSettings(filterKey);
                if (filterSettings.length === 0) return;

                filteredList = [
                    ...filteredList,
                    ...list.filter((entry) => {
                        const column = entry[filterSettings[0].filterKey];
                        return filterSettings[0].filterGroup[filterKey]
                            .map((filterType) =>
                                typeof filterType === 'string'
                                    ? filterType.toLowerCase()
                                    : filterType,
                            )
                            .includes(
                                typeof column === 'string'
                                    ? column.toLowerCase()
                                    : column,
                            );
                    }),
                ];
            });

            return filteredList;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filter, activeFilter, props.showAllWhenNoFilter],
    );

    const getData = useCallback(
        <T,>(list: Array<T>): Array<T> => {
            return sortData(filterData(list));
        },
        [sortData, filterData],
    );

    const contextState: ITableConsumerState<DataType, FilterType> = {
        id,
        exportName,
        page,
        rowsPerPage,
        rowsPerPageOptions,
        sortBy: currentSortBy,
        sortByDirection: currentSortByDirection,
        columns,
        filter,
        handleChangePage,
        handleChangeRowsPerPage,
        handleChangeSort,
        handleChangeFilter,
        paginate,
        sortData,
        filterData,
        highlightPhrase,
        onRowSelect,
        activeFilter,
        data: getData(data),
        rawData: data,
    };

    const { ...tableProps } = restProps;
    delete tableProps.showAllWhenNoFilter;

    const hasMorePages = Math.ceil(contextState.data.length / rowsPerPage) > 1;

    return (
        <TableContext.Provider value={contextState}>
            <div style={{ overflowX: 'auto' }}>
                <TableFilter />
                <MuiTable {...tableProps}>
                    <TableHead />
                    {contextState.data.length !== 0 ? (
                        <TableBody />
                    ) : (
                        <tbody>
                            <TableRow>
                                <TableCell colSpan={columns.length}>
                                    {!!emptyTableMessage && (
                                        <>{t(emptyTableMessage)}</>
                                    )}
                                </TableCell>
                            </TableRow>
                        </tbody>
                    )}
                    {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                    {(alwaysShowFooter || exportName || hasMorePages) && (
                        <MuiTableFooter>
                            <MuiTableRow>
                                {exportName && (
                                    <MuiTableCell>
                                        <TableExport />
                                    </MuiTableCell>
                                )}
                                <TablePagination
                                    colSpan={
                                        columns.length - (exportName ? 1 : 0)
                                    }
                                />
                            </MuiTableRow>
                        </MuiTableFooter>
                    )}
                </MuiTable>
            </div>
        </TableContext.Provider>
    );
};
